import { useReducedMotion } from 'framer-motion';

/**
 * A drawn city seen from above: parcels, a river, a park, and a road network
 * with real casing — a dark outline, a lighter surface, a dashed centre line.
 * Ambient traffic rides the roads via SVG `animateMotion` with `rotate="auto"`,
 * so vehicles bank into the curves instead of sliding sideways, and one tracked
 * van draws its violet trail from the depot to a destination pin.
 *
 * Everything shares the viewBox coordinate space, so the whole scene stays glued
 * together at any panel size. Decorative only — hidden from assistive tech.
 */

/* ── Geometry ──────────────────────────────────────────────────────────────
   The skeleton is deliberately orthogonal — cities are — and gets its character
   from the diagonal boulevard, the river, and the roundabout at (306, 470).
   Roads run past the viewBox edges so the network never looks like it stops. */

type Road = {
  id: string;
  d: string;
  /** Surface width. Casing is drawn 3 units wider underneath. */
  w: number;
  /** Arterials get a dashed centre line; smaller streets don't. */
  centre?: boolean;
  /** Half a lane, in viewBox units — how far traffic sits off the centre line. */
  lane: number;
};

const ROADS: Road[] = [
  // Arterials.
  { id: 'fm-a1', d: 'M 116 -40 L 116 840', w: 20, centre: true, lane: 5 },
  { id: 'fm-a2', d: 'M 458 -40 L 458 840', w: 20, centre: true, lane: 5 },
  { id: 'fm-b1', d: 'M -40 296 L 640 296', w: 20, centre: true, lane: 5 },
  {
    id: 'fm-bl',
    d: 'M -40 800 C 100 730 210 590 306 470 C 402 350 470 258 540 -40',
    w: 19,
    centre: true,
    lane: 4.8,
  },
  // Secondary streets.
  { id: 'fm-s1', d: 'M 306 -40 L 306 470', w: 14, lane: 3.4 },
  { id: 'fm-s2', d: 'M 306 470 L 306 840', w: 14, lane: 3.4 },
  { id: 'fm-h1', d: 'M -40 140 L 640 140', w: 14, lane: 3.4 },
  { id: 'fm-h2', d: 'M -40 470 L 640 470', w: 14, lane: 3.4 },
  { id: 'fm-h3', d: 'M -40 740 L 640 740', w: 14, lane: 3.4 },
  // Lanes. `fm-v1` stops at the arterial and `fm-e3` starts east of it so that
  // neither one drives through the park.
  { id: 'fm-v0', d: 'M 40 140 L 40 840', w: 10, lane: 0 },
  { id: 'fm-v1', d: 'M 210 -40 L 210 296', w: 10, lane: 0 },
  { id: 'fm-v2', d: 'M 382 140 L 382 740', w: 10, lane: 0 },
  { id: 'fm-v3', d: 'M 540 -40 L 540 140', w: 10, lane: 0 },
  { id: 'fm-v4', d: 'M 540 296 L 540 840', w: 10, lane: 0 },
  { id: 'fm-e1', d: 'M -40 68 L 640 68', w: 10, lane: 0 },
  { id: 'fm-e2', d: 'M -40 208 L 458 208', w: 10, lane: 0 },
  { id: 'fm-e3', d: 'M 306 386 L 640 386', w: 10, lane: 0 },
  { id: 'fm-e4', d: 'M -40 570 L 458 570', w: 10, lane: 0 },
  // A street down to the bank, and the crescent that curls off the arterial.
  { id: 'fm-l6', d: 'M 220 470 L 220 588', w: 9, lane: 0 },
  { id: 'fm-l7', d: 'M 458 560 C 520 578 556 610 566 660', w: 11, lane: 0 },
];

const RIVER = 'M -40 690 C 80 686 140 612 258 604 C 372 596 424 668 548 646 L 640 634';

const PARK = { x: 469, y: 151, w: 180, h: 134 };
const PARK_PATH = 'M 474 202 C 522 168 562 236 646 192';
/** Tree canopies, as [cx, cy, r]. Clustered along the path, thinner in the open. */
const PARK_TREES: [number, number, number][] = [
  [488, 174, 10], [509, 187, 7], [483, 197, 7.5],
  [620, 170, 9], [603, 182, 6.5], [629, 193, 7],
  [497, 263, 8], [517, 272, 6],
  [611, 262, 9], [630, 249, 6.5],
  [556, 270, 7], [563, 165, 6.5],
];

const ROUNDABOUT = { cx: 306, cy: 470, r: 26 };
const ROUNDABOUT_LOOP =
  'M 306 445 A 25 25 0 1 1 305.9 445.001 A 25 25 0 1 1 306 445';

const DEPOT = { x: 150, y: 222 };
const DESTINATION = { x: 458, y: 604 };

/** The tracked run: A1 south, B1 east, S1 south, H2 east, A2 south. Corners are
 *  rounded so the van doesn't snap through 90° in a single frame. */
const TRACKED_ROUTE =
  'M 116 222 L 116 278 Q 116 296 134 296 L 288 296 Q 306 296 306 314 ' +
  'L 306 452 Q 306 470 324 470 L 440 470 Q 458 470 458 488 L 458 604';

/** Normalising the route to 1000 units lets the trail's dash maths ignore the
 *  real arc length. Trail spans 150 of those units behind the van. */
const ROUTE_UNITS = 1000;
const TRAIL = 150;

const TRAFFIC_LIGHTS = [
  { x: 125, y: 149, begin: '0s' },
  { x: 315, y: 305, begin: '2.6s' },
  { x: 467, y: 479, begin: '5.1s' },
  { x: 125, y: 479, begin: '3.8s' },
];

/* ── City blocks ───────────────────────────────────────────────────────────
   Each cell between the arterials is split recursively into parcels, so the
   ground reads as buildings rather than as one flat rectangle. Seeded, so the
   layout is byte-identical on every render. */

type Rect = { x: number; y: number; w: number; h: number };

const hash = (seed: number) => {
  const v = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return v - Math.floor(v);
};

function subdivide(cell: Rect, seed: number): Rect[] {
  const out: Rect[] = [];
  const gap = 6;

  const walk = (r: Rect, depth: number, s: number) => {
    if (depth >= 3 || Math.max(r.w, r.h) < 46) {
      if (r.w > 10 && r.h > 10) out.push(r);
      return;
    }
    const ratio = 0.38 + hash(s) * 0.24;
    if (r.w >= r.h) {
      const cut = r.w * ratio;
      walk({ ...r, w: cut - gap / 2 }, depth + 1, s * 2 + 1);
      walk({ ...r, x: r.x + cut + gap / 2, w: r.w - cut - gap / 2 }, depth + 1, s * 2 + 2);
    } else {
      const cut = r.h * ratio;
      walk({ ...r, h: cut - gap / 2 }, depth + 1, s * 2 + 1);
      walk({ ...r, y: r.y + cut + gap / 2, h: r.h - cut - gap / 2 }, depth + 1, s * 2 + 2);
    }
  };

  walk(cell, 0, seed);
  return out;
}

const X_BANDS = [
  [-60, 40],
  [40, 116],
  [116, 210],
  [210, 306],
  [306, 382],
  [382, 458],
  [458, 540],
  [540, 660],
];
const Y_BANDS = [
  [-60, 68],
  [68, 140],
  [140, 208],
  [208, 296],
  [296, 386],
  [386, 470],
  [470, 570],
  [570, 740],
  [740, 860],
];

/** Kerb inset from the road centre lines. */
const KERB = 11;

/** The parks sit where cells would be, so those cells stay unbuilt. */
const PARK_COLS = [6, 7];
const PARK_ROWS = [2, 3];

const PARCELS: (Rect & { tone: number })[] = X_BANDS.flatMap((xb, col) =>
  Y_BANDS.flatMap((yb, row) => {
    if (PARK_COLS.includes(col) && PARK_ROWS.includes(row)) return [];
    const cell = {
      x: xb[0] + KERB,
      y: yb[0] + KERB,
      w: xb[1] - xb[0] - KERB * 2,
      h: yb[1] - yb[0] - KERB * 2,
    };
    return subdivide(cell, col * 17 + row * 5 + 3).map((r, i) => {
      // A little slack on each parcel so the frontages don't all line up.
      const slack = hash(col * 7 + row * 13 + i * 3) * 3;
      return {
        x: r.x + slack,
        y: r.y + slack,
        w: r.w - slack * 2,
        h: r.h - slack * 2,
        tone: hash(col * 31 + row * 11 + i),
      };
    });
  }),
);

/* ── Traffic ───────────────────────────────────────────────────────────────
   `keyPoints` drives where along the path the vehicle is at each `keyTime`, so
   repeating a point holds the vehicle there — a red light. Reversing the points
   sends it the other way, and because `rotate="auto"` follows the direction of
   travel, the lane offset stays on the vehicle's right either way. */

const PROFILES = {
  smooth: { keyPoints: [0, 1], keyTimes: [0, 1] },
  stopOnce: { keyPoints: [0, 0.46, 0.46, 1], keyTimes: [0, 0.4, 0.5, 1] },
  stopTwice: {
    keyPoints: [0, 0.3, 0.3, 0.68, 0.68, 1],
    keyTimes: [0, 0.24, 0.32, 0.62, 0.7, 1],
  },
} as const;

type Vehicle = {
  road: string;
  dur: number;
  /** Negative, so every road is already busy on the first frame. */
  begin: number;
  colour: string;
  shape: 'car' | 'van';
  reverse?: boolean;
  profile?: keyof typeof PROFILES;
  scale?: number;
};

const TRAFFIC: Vehicle[] = [
  { road: 'fm-a1', dur: 17, begin: -3, colour: 'var(--status-moving)', shape: 'car', profile: 'stopTwice' },
  { road: 'fm-a1', dur: 19, begin: -11, colour: 'var(--map-car-dark)', shape: 'car', reverse: true },
  { road: 'fm-a1', dur: 23, begin: -16, colour: 'var(--status-idle)', shape: 'van', profile: 'stopOnce' },
  { road: 'fm-a2', dur: 20, begin: -5, colour: 'var(--status-info)', shape: 'car' },
  { road: 'fm-a2', dur: 18, begin: -12, colour: 'var(--map-car-light)', shape: 'van', reverse: true, profile: 'stopOnce' },
  { road: 'fm-a2', dur: 25, begin: -21, colour: 'var(--status-moving)', shape: 'car', reverse: true },
  { road: 'fm-b1', dur: 21, begin: -2, colour: 'var(--status-moving)', shape: 'car', profile: 'stopTwice' },
  { road: 'fm-b1', dur: 25, begin: -14, colour: 'var(--status-idle)', shape: 'van', reverse: true },
  { road: 'fm-b1', dur: 17, begin: -8, colour: 'var(--map-car-dark)', shape: 'car' },
  { road: 'fm-bl', dur: 27, begin: -6, colour: 'var(--status-info)', shape: 'car' },
  { road: 'fm-bl', dur: 23, begin: -18, colour: 'var(--status-moving)', shape: 'car', reverse: true },
  { road: 'fm-h1', dur: 19, begin: -9, colour: 'var(--map-car-light)', shape: 'car', reverse: true, scale: 0.92 },
  { road: 'fm-h1', dur: 24, begin: -1, colour: 'var(--status-idle)', shape: 'car', scale: 0.92 },
  { road: 'fm-h2', dur: 20, begin: -4, colour: 'var(--status-info)', shape: 'van', profile: 'stopOnce', scale: 0.92 },
  { road: 'fm-h2', dur: 26, begin: -15, colour: 'var(--map-car-dark)', shape: 'car', reverse: true, scale: 0.92 },
  { road: 'fm-h3', dur: 22, begin: -13, colour: 'var(--status-moving)', shape: 'car', reverse: true, scale: 0.92 },
  { road: 'fm-s1', dur: 16, begin: -7, colour: 'var(--status-moving)', shape: 'car', scale: 0.92 },
  { road: 'fm-s2', dur: 18, begin: -10, colour: 'var(--map-car-light)', shape: 'car', reverse: true, scale: 0.92 },
  // Lanes: one car each, smaller, so the side streets tick over without noise.
  { road: 'fm-v0', dur: 21, begin: -5, colour: 'var(--map-car-dark)', shape: 'car', scale: 0.72 },
  { road: 'fm-v1', dur: 13, begin: -3, colour: 'var(--status-idle)', shape: 'car', reverse: true, scale: 0.72 },
  { road: 'fm-v2', dur: 19, begin: -11, colour: 'var(--status-info)', shape: 'car', scale: 0.72 },
  { road: 'fm-v4', dur: 22, begin: -8, colour: 'var(--map-car-light)', shape: 'car', reverse: true, scale: 0.72 },
  { road: 'fm-e1', dur: 24, begin: -2, colour: 'var(--status-moving)', shape: 'car', scale: 0.72 },
  { road: 'fm-e2', dur: 18, begin: -14, colour: 'var(--map-car-dark)', shape: 'car', reverse: true, scale: 0.72 },
  { road: 'fm-e3', dur: 15, begin: -6, colour: 'var(--status-idle)', shape: 'car', scale: 0.72 },
  { road: 'fm-e4', dur: 20, begin: -9, colour: 'var(--status-info)', shape: 'car', reverse: true, scale: 0.72 },
  { road: 'fm-v3', dur: 12, begin: -4, colour: 'var(--status-idle)', shape: 'car', scale: 0.72 },
  { road: 'fm-l7', dur: 11, begin: -2, colour: 'var(--map-car-light)', shape: 'car', scale: 0.72 },
];

const ROAD_BY_ID = new Map(ROADS.map((road) => [road.id, road]));

function Car({ colour, shape, scale = 1 }: Pick<Vehicle, 'colour' | 'shape' | 'scale'>) {
  const long = shape === 'van' ? 29 : 21;
  const wide = shape === 'van' ? 10 : 8.6;
  const half = long / 2;

  return (
    <g transform={scale === 1 ? undefined : `scale(${scale})`}>
      <ellipse cx={0.8} cy={1.8} rx={half + 1.4} ry={wide / 2 + 1} fill="var(--map-shadow)" />
      <rect x={-half} y={-wide / 2} width={long} height={wide} rx={3} fill={colour} />
      {/* Windscreen sits forward of centre, so you can read which way it's going
          even at the size a backdrop gets looked at. */}
      <rect
        x={shape === 'van' ? half - 7.6 : half - 8}
        y={-wide / 2 + 1.5}
        width={shape === 'van' ? 5.8 : 6.6}
        height={wide - 3}
        rx={1.6}
        fill="var(--map-glass)"
      />
      {shape === 'van' ? (
        <rect x={-half + 2.2} y={-wide / 2 + 2} width={long - 12.5} height={wide - 4} rx={1.4} fill="var(--map-glass)" opacity={0.28} />
      ) : (
        <rect x={-half + 2.4} y={-wide / 2 + 1.9} width={6} height={wide - 3.8} rx={1.4} fill="var(--map-glass)" opacity={0.55} />
      )}
      <rect x={half - 1.7} y={-wide / 2 + 1} width={1.7} height={1.8} rx={0.7} fill="var(--map-lamp)" />
      <rect x={half - 1.7} y={wide / 2 - 2.8} width={1.7} height={1.8} rx={0.7} fill="var(--map-lamp)" />
    </g>
  );
}

function Motion({
  road,
  dur,
  begin,
  reverse,
  profile = 'smooth',
  rotate = true,
}: {
  road: string;
  dur: number;
  begin: number;
  reverse?: boolean;
  profile?: keyof typeof PROFILES;
  rotate?: boolean;
}) {
  const { keyPoints, keyTimes } = PROFILES[profile];
  const points = reverse ? keyPoints.map((p) => 1 - p) : keyPoints;

  return (
    <animateMotion
      dur={`${dur}s`}
      begin={`${begin}s`}
      repeatCount="indefinite"
      calcMode="linear"
      keyPoints={points.join(';')}
      keyTimes={keyTimes.join(';')}
      rotate={rotate ? 'auto' : undefined}
    >
      <mpath href={`#${road}`} />
    </animateMotion>
  );
}

const HERO_DUR = 18;

/** Where the tracked van parks when the viewer has asked for reduced motion —
 *  on the final leg of the route, just short of the destination — and how far
 *  along the normalised route that is, so the static trail ends underneath it. */
const STILL_VAN = { x: 458, y: 552 };
const STILL_PROGRESS = 925;

export function AuthBackdrop() {
  const still = useReducedMotion();

  return (
    <div
      className="fleet-map pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      <div
        className="absolute inset-0"
        style={{
          WebkitMaskImage:
            'radial-gradient(150% 110% at 46% 34%, #000 62%, transparent 100%)',
          maskImage:
            'radial-gradient(150% 110% at 46% 34%, #000 62%, transparent 100%)',
        }}
      >
        <svg
          viewBox="0 0 600 800"
          className="absolute inset-0 size-full"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            {/* The river carves the parcels rather than sitting on top of them. */}
            <mask id="fm-land">
              <rect x={-60} y={-60} width={760} height={980} fill="#fff" />
              <path d={RIVER} fill="none" stroke="#000" strokeWidth={46} strokeLinecap="round" />
            </mask>

            <linearGradient id="fm-trail" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="1" />
            </linearGradient>

            <filter id="fm-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="4" />
            </filter>

            {ROADS.map((road) => (
              <path key={road.id} id={road.id} d={road.d} fill="none" />
            ))}
            <path id="fm-roundabout-loop" d={ROUNDABOUT_LOOP} fill="none" />
            <path id="fm-route" d={TRACKED_ROUTE} fill="none" pathLength={ROUTE_UNITS} />
          </defs>

          {/* Water first — everything else is built on the land above it. */}
          <path d={RIVER} fill="none" stroke="var(--map-water)" strokeWidth={42} strokeLinecap="round" />
          <path d={RIVER} fill="none" stroke="var(--map-water)" strokeWidth={22} strokeLinecap="round" opacity={0.55} />

          <g mask="url(#fm-land)">
            {PARCELS.map((parcel) => (
              <rect
                key={`${parcel.x}-${parcel.y}-${parcel.w}`}
                x={parcel.x}
                y={parcel.y}
                width={parcel.w}
                height={parcel.h}
                rx={3}
                fill={parcel.tone > 0.82 ? 'var(--map-parcel-alt)' : 'var(--map-parcel)'}
                opacity={0.55 + parcel.tone * 0.45}
              />
            ))}
          </g>

          {/* Park. */}
          <rect {...PARK} rx={10} fill="var(--map-park)" />
          <path
            d={PARK_PATH}
            fill="none"
            stroke="var(--map-path)"
            strokeWidth={2.2}
            strokeDasharray="3 5"
            strokeLinecap="round"
          />
          {PARK_TREES.map(([cx, cy, r]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} fill="var(--map-tree)" />
          ))}

          {/* Roads, in three passes so every casing sits under every surface and
              junctions merge instead of stacking outlines on top of each other. */}
          <g strokeLinecap="round" strokeLinejoin="round" fill="none">
            {ROADS.map((road) => (
              <path key={`c-${road.id}`} d={road.d} stroke="var(--map-casing)" strokeWidth={road.w + 3.5} />
            ))}
            <circle
              cx={ROUNDABOUT.cx}
              cy={ROUNDABOUT.cy}
              r={ROUNDABOUT.r}
              stroke="var(--map-casing)"
              strokeWidth={20}
            />
            {ROADS.map((road) => (
              <path key={`s-${road.id}`} d={road.d} stroke="var(--map-surface)" strokeWidth={road.w} />
            ))}
            <circle
              cx={ROUNDABOUT.cx}
              cy={ROUNDABOUT.cy}
              r={ROUNDABOUT.r}
              stroke="var(--map-surface)"
              strokeWidth={16}
            />
            {ROADS.filter((road) => road.centre).map((road) => (
              <path
                key={`m-${road.id}`}
                d={road.d}
                stroke="var(--map-line)"
                strokeWidth={1.1}
                strokeDasharray="9 11"
                opacity={0.5}
              />
            ))}
          </g>

          {/* Roundabout island. */}
          <circle cx={ROUNDABOUT.cx} cy={ROUNDABOUT.cy} r={ROUNDABOUT.r - 11} fill="var(--map-park)" />

          {!still &&
            TRAFFIC_LIGHTS.map((light) => (
              <circle key={`${light.x}-${light.y}`} cx={light.x} cy={light.y} r={2.6} fill="var(--status-moving)">
                <animate
                  attributeName="fill"
                  calcMode="discrete"
                  values="var(--status-moving);var(--status-idle);var(--status-alert)"
                  keyTimes="0;0.6;0.72"
                  dur="9s"
                  begin={light.begin}
                  repeatCount="indefinite"
                />
              </circle>
            ))}

          {/* Ambient traffic. Muted, so the tracked van stays the subject. */}
          {!still && (
            <g opacity={0.85}>
              {TRAFFIC.map((vehicle, index) => {
                const road = ROAD_BY_ID.get(vehicle.road);
                if (!road) return null;
                return (
                  <g key={`${vehicle.road}-${index}`}>
                    <g transform={`translate(0 ${road.lane})`}>
                      <Car colour={vehicle.colour} shape={vehicle.shape} scale={vehicle.scale} />
                    </g>
                    <Motion
                      road={vehicle.road}
                      dur={vehicle.dur}
                      begin={vehicle.begin}
                      reverse={vehicle.reverse}
                      profile={vehicle.profile}
                    />
                  </g>
                );
              })}

              {[0, -4.5].map((begin) => (
                <g key={`rb-${begin}`}>
                  <Car colour="var(--status-offline)" shape="car" scale={0.75} />
                  <Motion road="fm-roundabout-loop" dur={9} begin={begin} />
                </g>
              ))}
            </g>
          )}

          {/* ── The tracked run ────────────────────────────────────────────── */}

          {/* Planned route: dotted, quiet, shows where the van is headed. */}
          <path
            d={TRACKED_ROUTE}
            fill="none"
            stroke="var(--primary)"
            strokeWidth={3}
            strokeDasharray="0.5 9"
            strokeLinecap="round"
            opacity={0.4}
          />

          {/* Travelled trail: a fixed-length dash whose offset tracks the van, so
              the glow always ends exactly under it. */}
          <g>
            <path
              d={TRACKED_ROUTE}
              fill="none"
              pathLength={ROUTE_UNITS}
              stroke="var(--primary)"
              strokeWidth={11}
              strokeLinecap="round"
              opacity={0.34}
              filter="url(#fm-glow)"
              strokeDasharray={`${TRAIL} ${ROUTE_UNITS}`}
              strokeDashoffset={still ? TRAIL - STILL_PROGRESS : TRAIL}
            >
              {!still && (
                <animate
                  attributeName="stroke-dashoffset"
                  values={`${TRAIL};${TRAIL - ROUTE_UNITS}`}
                  dur={`${HERO_DUR}s`}
                  repeatCount="indefinite"
                  calcMode="linear"
                />
              )}
            </path>
            <path
              d={TRACKED_ROUTE}
              fill="none"
              pathLength={ROUTE_UNITS}
              stroke="url(#fm-trail)"
              strokeWidth={5}
              strokeLinecap="round"
              strokeDasharray={`${TRAIL} ${ROUTE_UNITS}`}
              strokeDashoffset={still ? TRAIL - STILL_PROGRESS : TRAIL}
            >
              {!still && (
                <animate
                  attributeName="stroke-dashoffset"
                  values={`${TRAIL};${TRAIL - ROUTE_UNITS}`}
                  dur={`${HERO_DUR}s`}
                  repeatCount="indefinite"
                  calcMode="linear"
                />
              )}
            </path>
          </g>

          {/* Depot — origin marker, paired with the destination pin. */}
          <g transform={`translate(${DEPOT.x} ${DEPOT.y})`}>
            <circle r={17} fill="var(--primary)" opacity={0.14} />
            <circle r={11} fill="var(--map-surface)" />
            <circle r={11} fill="none" stroke="var(--primary)" strokeWidth={2.6} />
            <circle r={4} fill="var(--primary)" />
            <g transform="translate(-24 -35)">
              <rect x={0} y={0} width={48} height={15} rx={4.5} fill="var(--map-chip)" />
              <text
                x={24}
                y={10.6}
                fontSize={7.6}
                fontWeight={600}
                textAnchor="middle"
                fill="var(--foreground)"
              >
                DEPOT 04
              </text>
            </g>
          </g>

          {/* Destination pin. */}
          <g transform={`translate(${DESTINATION.x} ${DESTINATION.y})`}>
            <ellipse cx={0} cy={1} rx={7} ry={2.6} fill="var(--primary)" opacity={0.18}>
              {!still && (
                <animate attributeName="rx" values="5;10;5" dur="3s" repeatCount="indefinite" />
              )}
            </ellipse>
            <path
              d="M 0 0 C -5.5 -7.5 -8 -11 -8 -15 A 8 8 0 1 1 8 -15 C 8 -11 5.5 -7.5 0 0 Z"
              fill="var(--primary)"
            />
            <circle cx={0} cy={-15} r={3} fill="var(--primary-foreground)" />
          </g>

          {/* The tracked van, plus a chip that rides along without rotating. */}
          <g transform={still ? `translate(${STILL_VAN.x} ${STILL_VAN.y})` : undefined}>
            <circle r={still ? 11 : 0} fill="var(--primary)" opacity={0.25}>
              {!still && (
                <>
                  <animate attributeName="r" values="7;20" dur="2.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.28;0" dur="2.6s" repeatCount="indefinite" />
                </>
              )}
            </circle>
            {!still && <Motion road="fm-route" dur={HERO_DUR} begin={0} rotate={false} />}
          </g>

          <g transform={still ? `translate(${STILL_VAN.x} ${STILL_VAN.y}) rotate(90)` : undefined}>
            <g transform="scale(1.32)">
            <ellipse cx={0.4} cy={1.2} rx={9.4} ry={3.6} fill="var(--map-shadow)" />
            <rect x={-8} y={-2.9} width={16} height={5.8} rx={1.9} fill="var(--primary)" />
            <rect x={2.6} y={-2} width={4} height={4} rx={1} fill="var(--map-glass)" />
            <rect x={-6.6} y={-1.7} width={7.6} height={3.4} rx={0.9} fill="var(--primary-foreground)" opacity={0.32} />
            <rect x={7.1} y={-2.4} width={0.9} height={1} rx={0.4} fill="var(--map-lamp)" />
            <rect x={7.1} y={1.4} width={0.9} height={1} rx={0.4} fill="var(--map-lamp)" />
            </g>
            {!still && <Motion road="fm-route" dur={HERO_DUR} begin={0} />}
          </g>

          {/* Unit chip. It rides the same path without `rotate`, so it stays
              upright and readable while the van turns underneath it. */}
          <g
            opacity={0.92}
            transform={still ? `translate(${STILL_VAN.x} ${STILL_VAN.y})` : undefined}
          >
            <g transform="translate(13 -22)">
              <rect x={0} y={0} width={76} height={17} rx={5} fill="var(--map-chip)" />
              <circle cx={9.5} cy={8.5} r={2.6} fill="var(--status-moving)" />
              <text x={17} y={11.6} fontSize={8.2} fontWeight={600} fill="var(--foreground)">
                OF-2214 · 62 km/h
              </text>
            </g>
            {!still && <Motion road="fm-route" dur={HERO_DUR} begin={0} rotate={false} />}
          </g>
        </svg>
      </div>

      {/* Brand wash over the whole plate, and a scrim at the foot of the panel so
          the copy underneath never fights the city for attention. */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_28%_18%,color-mix(in_oklch,var(--primary)_14%,transparent),transparent_58%)]" />
      <div className="absolute inset-x-0 bottom-0 h-[52%] bg-gradient-to-t from-background/85 via-background/28 to-transparent" />
    </div>
  );
}
